// src/components/oracle/OracleDeckGrid.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { oracleDeck, uiText } from '../../data/oracle/oracleDeckData';
import { MagnifyingGlassIcon, ArrowUpIcon } from '@heroicons/react/24/solid';
import { useGlow } from '../../hooks/useGlow';

const OracleDeckGrid = ({ lang = 'es' }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isScrollButtonVisible, setIsScrollButtonVisible] = useState(false);
  const cardRefs = useRef({});
  const searchContainerRef = useRef(null);
  const ui = uiText[lang] || uiText['es'];

  const titleStyle = useGlow({
    gradient: 'linear-gradient(to bottom, #e0e0e0ff, #9b977aff, #9e823cff, #776f5cff)',
    intensityMin: 0.2, intensityMax: 0.4, intervalDuration: 3000,
  });
  
  const symbolStyle = useGlow({
    gradient: 'linear-gradient(to bottom, #e0e0e0ff, #a3a190ff, #e4d09fff, #c9bea3ff)',
    intensityMin: 0.2, intensityMax: 0.4, intervalDuration: 3000,
  });

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const handleScroll = () => setIsScrollButtonVisible(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm) {
      const lowerTerm = debouncedSearchTerm.toLowerCase().trim();
      const foundCard = oracleDeck.find(c => {
        const matchesId = c.id.toString() === lowerTerm;
        const matchesNameEs = c.es?.name?.toLowerCase().includes(lowerTerm);
        const matchesNameEn = c.en?.name?.toLowerCase().includes(lowerTerm);
        return matchesId || matchesNameEs || matchesNameEn;
      });

      if (foundCard && cardRefs.current[foundCard.id]) {
        const scrollTimeout = setTimeout(() => {
          cardRefs.current[foundCard.id]?.scrollIntoView({
            behavior: 'smooth', block: 'center', inline: 'center'
          });
        }, 550);
        return () => clearTimeout(scrollTimeout);
      }
    }
  }, [debouncedSearchTerm]);

  const handleCardClick = (card) => {
    let nameLanguage = lang === 'es' ? 'oraculo' : 'oracle';
    let idRef = card.idName || card.id;
    navigate(`/${nameLanguage}/${idRef}`);
  };

  const highlightedIndex = debouncedSearchTerm ? oracleDeck.findIndex(c => {
    const lowerTerm = debouncedSearchTerm.toLowerCase().trim();
    return (
        c.id.toString() === lowerTerm ||
        c.es?.name?.toLowerCase().includes(lowerTerm) ||
        c.en?.name?.toLowerCase().includes(lowerTerm)
    );
  }) : -1;

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-8" id="oracle-deck">
      <div className="text-center mb-8">
        <h1 style={titleStyle} className="text-4xl md:text-5xl font-bold mb-4">{ui.title}</h1>
        <p className="text-lg text-gray-400 max-w-3xl mx-auto">{ui.intro}</p>
      </div>
      
      <div ref={searchContainerRef} className="mb-8 max-w-lg mx-auto">
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={ui.searchPlaceholder || "Buscar por ID o Nombre..."}
            className="w-full bg-gray-800/60 border-2 border-gray-700 text-white rounded-lg py-3 pl-12 pr-4 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-300"
          />
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-500" />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
        {oracleDeck.map((card, index) => {
          const cardContent = card[lang];
          
          // NOTA: Se agregó 'relative' para contener los elementos absolutos dentro de cada carta
          let styleClasses = 'group relative cursor-pointer aspect-w-3 aspect-h-4 bg-gray-800 border-2 rounded-xl overflow-hidden transform transition-all duration-500 ease-in-out';
          
          if (highlightedIndex !== -1) {
            const distance = Math.abs(index - highlightedIndex);
            if (distance === 0) styleClasses += ' scale-110 opacity-100 border-sky-400 shadow-2xl shadow-sky-500/50 z-10';
            else if (distance === 1) styleClasses += ' scale-95 opacity-70 border-gray-700';
            else if (distance < 4) styleClasses += ' scale-90 opacity-40 border-gray-800';
            else styleClasses += ' scale-80 opacity-20 border-gray-800';
          } else {
            styleClasses += ' border-gray-700 hover:scale-105 hover:border-sky-500 hover:shadow-2xl hover:shadow-sky-500/30';
          }

          return (
            <div 
              key={card.id} 
              ref={el => cardRefs.current[card.id] = el} 
              onClick={() => handleCardClick(card)} 
              className={styleClasses}
            >
              <img 
                src={card.image} 
                alt={cardContent.name} 
                className="w-full h-full object-cover transition-transform duration-300 group-hover:opacity-50"
              />
              
              {/* Número de la carta */}
              <div className="absolute top-2 left-2 z-20">
                 <span style={symbolStyle} className="px-2 py-1 rounded-lg text-xs font-bold text-sky-400 shadow-sm">
                    #{card.id}
                 </span>
              </div>

              {/* Nombre de la carta (contenido en la base de SU carta) */}
              <div className="absolute inset-x-0 bottom-0 pt-8 pb-3 bg-gradient-to-t from-black/90 via-black/60 to-transparent flex items-end justify-center transition-opacity duration-300 z-20">
                <h3 style={symbolStyle} className="text-xs md:text-sm font-bold text-white text-center px-2 drop-shadow-md">
                  {cardContent.name}
                  {cardContent.name2 && <><br />{cardContent.name2}</>}
                  {cardContent.name3 && <><br />{cardContent.name3}</>}
                </h3>
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={() => searchContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
        className={`fixed bottom-6 right-6 z-50 p-3 rounded-full bg-gradient-to-br from-sky-500 to-purple-600
                   text-white shadow-lg transition-all duration-300 hover:scale-110
                   ${isScrollButtonVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50 pointer-events-none'}`}
      >
        <ArrowUpIcon className="w-6 h-6" />
      </button>
    </div>
  );
};

export default OracleDeckGrid;